/**
 * Which valuation method suits this business, and which ones we can run.
 *
 * A discounted cash flow is the right anchor for a manufacturer and close to
 * meaningless for a bank, whose engine is equity capital turned into loans at a
 * return on equity. The anchors come first and are marked; the rest follow.
 *
 * A method the accounts cannot support says what it needs instead of showing a
 * number. That distinction is the point: a set of accounts contains earnings,
 * book value, EBITDA and revenue, and nothing about reserves in the ground, a
 * drug pipeline or annual recurring revenue. A plausible-looking rNPV derived
 * from a balance sheet would be a fabrication, and one nobody downstream could
 * catch.
 */

import { useState } from 'react';
import { formatMoney } from '../../../lib/currency';
import PriceScenarios from './PriceScenarios';
import type { MethodResult, MethodsComputed } from '../../../types/financial-model';
import {
  useExplanations,
  type MethodExplanation,
} from './methodExplanations';

const BUSINESS_LABELS: Record<string, string> = {
  general: 'General trading company',
  bank: 'Bank',
  insurer: 'Insurer',
  manufacturer: 'Manufacturer',
  saas: 'SaaS',
  reit: 'REIT / property',
  mining: 'Mining',
  biotech: 'Biotech',
  asset_manager: 'Asset manager',
  conglomerate: 'Conglomerate',
  distressed: 'Distressed',
  utility: 'Utility (regulated)',
  startup: 'Startup / pre-profit',
  high_growth_tech: 'High-growth tech',
  commodity_holder: 'Commodity holder (bullion, trust)',
};

function MethodRow({
  entry,
  currency,
  explanation,
}: {
  entry: MethodResult;
  currency: string;
  explanation?: MethodExplanation;
}) {
  const [open, setOpen] = useState(false);
  const available = entry.equityValue != null;

  return (
    <div className="border-t border-gray-100 py-2 first:border-t-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex items-baseline gap-2 text-sm text-gray-800">
          {entry.label}
          {entry.recommended && (
            <span className="rounded bg-gray-900 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
              Anchor
            </span>
          )}
        </span>
        <span
          className={
            available
              ? 'shrink-0 text-sm font-medium tabular-nums text-gray-900'
              : 'shrink-0 text-xs text-gray-400'
          }
        >
          {available
            ? formatMoney(entry.equityValue as number, currency)
            : 'not available'}
        </span>
      </div>
      {available && entry.perShare != null && (
        <div className="text-xs text-gray-500">
          {formatMoney(entry.perShare, currency, { decimals: 2 })} per share
        </div>
      )}
      {/* One or the other, never both: a computed method explains how it got
          there, an unavailable one says what would let it run. */}
      <div className="mt-0.5 text-xs leading-snug text-gray-500">
        {available ? entry.basis : `Needs ${entry.needs ?? 'data the accounts do not hold'}`}
      </div>
      {available && (
        <PriceScenarios
          scenarios={entry.scenarios}
          format={(value) => formatMoney(value, currency)}
        />
      )}

      {/* WHAT THE METHOD IS, behind a disclosure.
          The line above says what the arithmetic did with THIS company's figures; it
          does not say what residual income measures or when to distrust it. Both were
          missing, and a reader who does not know the method cannot judge the number.
          Collapsed because fourteen explanations open at once would bury the figures,
          and the figures are what somebody came for. */}
      {explanation && (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            className="text-xs text-gray-500 underline decoration-dotted hover:text-gray-800"
          >
            {open ? 'Hide how this works' : 'How this method works'}
          </button>
          {open && (
            <div className="mt-1.5 space-y-1.5 border-l-2 border-gray-200 pl-3 text-xs leading-relaxed text-gray-600">
              <p>{explanation.whatItIs}</p>
              <p>{explanation.howItWorks}</p>
              {/* Always shown with the rest, never collapsed separately. A tool that
                  explained each method as though it were the right one would be used
                  to justify whichever answer was largest. */}
              <p className="text-amber-800">
                <span className="font-medium">Where it misleads: </span>
                {explanation.misleads}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MethodsPanelV2({
  methods,
  currency,
}: {
  methods: MethodsComputed | null | undefined;
  currency: string;
}) {
  // Before the early return, because hooks must not be conditional. Fetched once per
  // session and shared, so a panel that renders twice makes one request.
  const explanations = useExplanations();

  if (!methods || methods.results.length === 0) return null;

  const available = methods.results.filter((r) => r.equityValue != null);
  const unavailable = methods.results.filter((r) => r.equityValue == null);

  return (
    <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
      <h4 className="text-sm font-semibold text-gray-900">
        Methods for a {BUSINESS_LABELS[methods.businessType] ?? methods.businessType}
      </h4>
      <p className="mt-0.5 text-xs text-gray-500">
        {methods.engine}. {methods.why}
      </p>

      <div className="mt-3">
        {available.map((entry) => (
          <MethodRow
              key={entry.method}
              entry={entry}
              currency={currency}
              explanation={explanations[entry.method]}
            />
        ))}
      </div>

      {unavailable.length > 0 && (
        <div className="mt-4">
          <h5 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
            Needs data the accounts do not contain
          </h5>
          <div className="mt-1">
            {unavailable.map((entry) => (
              <MethodRow
              key={entry.method}
              entry={entry}
              currency={currency}
              explanation={explanations[entry.method]}
            />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Figures the engine refused to credit, and why. */
export function ValuationWarningsV2({ warnings }: { warnings?: string[] }) {
  if (!warnings || warnings.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-800">
        Worth knowing about this valuation
      </h4>
      <ul className="mt-1 space-y-1">
        {warnings.map((note) => (
          <li key={note} className="text-xs leading-snug text-amber-900">
            {note}
          </li>
        ))}
      </ul>
    </div>
  );
}
